import { FieldValues } from 'react-hook-form'
import { WalletConditionSectionProps } from './types'
import { WithdrawalLimitsFields } from './components/WithdrawalLimitsFields'
import { TurnoverField } from './components/TurnoverField'
import { GameSettingsFields } from './components/GameSettingsFields'
import { PresetConfigFields } from './components/PresetConfigFields'
import { TriangleAlert } from 'lucide-react'
import ProviderVersionSettings from '@/common/components/ProviderVersionSettings'
import { RtpGuideButton } from '@/modules/promotion/components/RtpGuideModal'
import { RtpPresetPrecedenceTooltip } from '@/common/components/RtpPresetPrecedenceTooltip'

export function WalletConditionSection<
	TFieldValues extends FieldValues = FieldValues,
>({
	control,
	fieldPrefix = '',
	showGameSettings = true,
	showPoolPreset = true,
	hideMaximumWithdraw = false,
	hideProviderVersionSettings = false,
	turnoverRound,
}: WalletConditionSectionProps<TFieldValues>) {
	const isPgFeaturesDisabled =
		import.meta.env.VITE_DISABLED_PG_FEATURES === 'true'
	const showPresetSection = showPoolPreset && !isPgFeaturesDisabled

	return (
		<div className="space-y-6 sm:space-y-8">
			{/* Withdrawal Limits Section */}
			<div className="space-y-4 rounded-lg border p-4 sm:p-6">
				<h2 className="text-base font-semibold sm:text-lg">ข้อจำกัดการถอน</h2>
				<WithdrawalLimitsFields
					control={control}
					fieldPrefix={fieldPrefix}
					hideMaximum={hideMaximumWithdraw}
				/>
			</div>

			{/* Turnover Section */}
			<div className="space-y-4 rounded-lg border p-4 sm:p-6">
				<h2 className="text-base font-semibold sm:text-lg">เทิร์นโอเวอร์</h2>
				<TurnoverField control={control} fieldPrefix={fieldPrefix} />
			</div>

			{/* Provider Version Settings Section */}
			{!hideProviderVersionSettings && (
				<div className="space-y-4 rounded-lg border p-4 sm:p-6">
					<ProviderVersionSettings
						control={control}
						fieldPrefix={fieldPrefix}
					/>
				</div>
			)}

			{/* Game Settings Section */}
			{showGameSettings && (
				<div className="space-y-4 rounded-lg border p-4 sm:p-6">
					<h2 className="text-base font-semibold sm:text-lg">การตั้งค่าเกม</h2>
					<GameSettingsFields control={control} fieldPrefix={fieldPrefix} />
				</div>
			)}

			{/* Pool and Preset Configuration Section */}
			{showPresetSection && (
				<div className="space-y-4 rounded-lg border p-4 sm:space-y-6 sm:p-6">
					<div className="space-y-3">
						<div className="flex items-center gap-2">
							<h2 className="text-lg font-semibold sm:text-xl">
								การตั้งค่า RTP
							</h2>
							<RtpPresetPrecedenceTooltip highlightLevel={3} />
						</div>
						<div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
							<TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
							<div className="flex-1 text-amber-800 dark:text-amber-200">
								<p className="font-medium">โปรดตั้งค่า RTP ในทุกโปรโมชั่น</p>
								<p className="mt-0.5">
									โปรโมชั่นหรือกิจกรรมที่ให้โบนัสหรือเครดิตฟรีสูงควรตั้งค่า RTP
									ต่ำ (กินเยอะ) เพื่อให้ผู้เล่นเครดิตน้อยกว่าเงินที่ฝากมา
									หลังทำการเคลียร์เทิร์นครบ หากตั้งค่า RTP สูงเกินไป
									ระบบจะขาดทุนในระยะยาวเนื่องจากต้นทุนสูงกว่ารายได้จากการกินผู้เล่น{' '}
									<strong>
										และต้องแน่ใจว่าค่ายเกมที่เลือกรองรับระบบปรับแตก
										มิฉะนั้นการตั้งค่า RTP จะไม่มีผล และทำให้เราขาดทุนได้
									</strong>
								</p>
								<div className="mt-3">
									<RtpGuideButton />
								</div>
							</div>
						</div>
					</div>
					<div className="space-y-4 sm:space-y-6">
						<PresetConfigFields
							control={control}
							fieldPrefix={fieldPrefix}
							turnoverRound={turnoverRound}
						/>
					</div>
				</div>
			)}
		</div>
	)
}

export { walletConditionSchema } from './schema'
export type { WalletConditionFormValues } from './types'
export { prepareWalletConditionForSubmit } from './utils'
